import { useState, useEffect, useRef } from 'react';
import { Lightbulb, Plus, CheckCircle2, Sparkles, FileText, ChevronDown, Send } from 'lucide-react';

// ─── Keyword-basierte Kategorieerkennung (clientseitig, kein API-Call) ───

const KATEGORIEN = [
  {
    id: 'borkenkafer',
    keywords: ['käfer', 'borkenkäfer', 'buchdrucker', 'kupferstecher', 'bohrmehl', 'braune krone', 'rindenbrut', 'pheromonfalle'],
    label: 'Borkenkäfer / Schädlinge',
    tipps: ['Fläche in Hektar', 'Bundesland', 'Alter des Bestands', 'Seit wann bemerkt?'],
    vorlage: 'Borkenkäferbefall auf ___ ha Fichtenbestand in ___. Bestand ca. ___ Jahre alt. Befall seit ___. Sofortige Aufarbeitung und Abfuhr nötig.',
  },
  {
    id: 'durchforstung',
    keywords: ['durchforst', 'zu dicht', 'auslicht', 'zu eng steh', 'bestandspflege', 'vorratspflege'],
    label: 'Durchforstung',
    tipps: ['Fläche in Hektar', 'Bundesland', 'Baumart und Alter', 'Harvester möglich?'],
    vorlage: '___ ha Bestand in ___, ca. ___ Jahre alt. Durchforstung überfällig, Bäume stehen zu dicht. Z-Bäume sollen freigestellt werden.',
  },
  {
    id: 'aufforstung',
    keywords: ['aufforst', 'pflanz', 'wiederaufforst', 'waldumbau', 'kahlfläche', 'erstaufforst', 'mischwald'],
    label: 'Aufforstung / Waldumbau',
    tipps: ['Fläche in Hektar', 'Bundesland', 'Geplante Baumarten', 'Förderung gewünscht?'],
    vorlage: '___ ha in ___ sollen aufgeforstet werden. Geplante Baumarten: ___. Fördermöglichkeiten gewünscht.',
  },
  {
    id: 'sturmschaden',
    keywords: ['sturm', 'windwurf', 'windbruch', 'sturmholz', 'schneebruch', 'kalamität', 'sturmschad'],
    label: 'Sturmschäden / Kalamität',
    tipps: ['Geschätzte Festmeter', 'Bundesland', 'Gelände (Hang/flach)', 'Zufahrt vorhanden?'],
    vorlage: 'Sturmschäden auf ca. ___ ha in ___. Geschätzt ___ Festmeter Sturmholz. Gelände: ___. Zufahrt: ___. Aufarbeitung und Abfuhr nötig.',
  },
  {
    id: 'holzernte',
    keywords: ['holzernt', 'einschlag', 'fällung', 'fällen', 'harvester', 'holz mach', 'seilkran', 'motormanuell'],
    label: 'Holzernte',
    tipps: ['Fläche und Festmeter', 'Bundesland', 'BHD der Bäume', 'Gelände (Hang/flach)'],
    vorlage: '___ ha Bestand in ___, ca. ___ Jahre. Holzernte geplant. BHD ca. ___ cm. Gelände: ___. Ca. ___ Festmeter.',
  },
  {
    id: 'neophyten',
    keywords: ['traubenkirsche', 'robinie', 'götterbaum', 'neophyt', 'invasiv', 'bekämpf'],
    label: 'Invasive Arten',
    tipps: ['Fläche in Hektar', 'Bundesland', 'Welche Art genau?', 'Bisherige Maßnahmen?'],
    vorlage: '___ ha in ___, starker Befall mit ___. Verdrängt heimische Verjüngung. Professionelle Bekämpfung gesucht.',
  },
  {
    id: 'foerderung',
    keywords: ['förder', 'förderantrag', 'fördermittel', 'gak', 'waldföpr', 'zuschuss', 'förderprogramm'],
    label: 'Förderung',
    tipps: ['Bundesland', 'Welche Maßnahme?', 'Fläche in Hektar', 'Bereits Kontakt zum Forstamt?'],
    vorlage: 'Förderantrag für ___ auf ___ ha in ___. Beratung zu Förderprogrammen und Antragstellung gewünscht.',
  },
  {
    id: 'wildschaden',
    keywords: ['verbiss', 'wildverbiss', 'schälschad', 'wildschad', 'wuchshüll', 'zaun', 'wildschutz'],
    label: 'Wildschäden',
    tipps: ['Fläche und Baumarten', 'Bundesland', 'Art des Schadens', 'Bisherige Schutzmaßnahmen?'],
    vorlage: '___ ha in ___, starker Wildverbiss an ___. Schutzmaßnahmen benötigt.',
  },
  {
    id: 'verkauf',
    keywords: ['verkauf', 'waldwert', 'waldverkauf', 'holzverkauf', 'holzpreis', 'wert ermittl'],
    label: 'Holz-/Waldverkauf',
    tipps: ['Fläche in Hektar', 'Bundesland', 'Baumart und Alter', 'Grund des Verkaufs?'],
    vorlage: '___ ha Wald in ___, Bestand: ___. Werteinschätzung oder Verkaufsberatung gewünscht.',
  },
  {
    id: 'erbschaft',
    keywords: ['geerbt', 'erbschaft', 'erbe', 'erben', 'übernommen', 'nachlass'],
    label: 'Wald geerbt',
    tipps: ['Ungefähre Fläche', 'Bundesland', 'Eigene Vorkenntnisse?', 'Bewirtschaftung bisher?'],
    vorlage: 'Wald geerbt, ca. ___ ha in ___. Keine Vorkenntnisse in Forstwirtschaft. Erste Orientierung gewünscht.',
  },
];

// Schlüsselinfos die in jedem Fall wichtig sind
const SCHLUESSELINFOS = [
  { id: 'bundesland', keywords: ['bayern', 'baden-württemberg', 'niedersachsen', 'nordrhein-westfalen', 'hessen', 'sachsen', 'thüringen', 'brandenburg', 'rheinland-pfalz', 'schleswig-holstein', 'sachsen-anhalt', 'mecklenburg-vorpommern', 'saarland', 'bremen', 'hamburg', 'berlin', 'nrw', 'bw'], label: 'Bundesland' },
  { id: 'flaeche', keywords: ['hektar', ' ha ', ' ha,', ' ha.', 'fläche', 'morgen', 'quadratmeter', 'ar '], label: 'Fläche' },
  { id: 'baumart', keywords: ['fichte', 'kiefer', 'buche', 'eiche', 'tanne', 'douglasie', 'lärche', 'ahorn', 'esche', 'birke', 'erle', 'pappel'], label: 'Baumart' },
  { id: 'dringlichkeit', keywords: ['dringend', 'sofort', 'akut', 'schnell', 'eilig', 'dieses jahr', 'nächste woche', 'flexibel', 'keine eile'], label: 'Dringlichkeit' },
];

function analyzeText(text) {
  if (!text || text.length < 5) return null;

  const lower = text.toLowerCase();

  // Kategorie erkennen
  let bestKategorie = null;
  let bestScore = 0;
  for (const kat of KATEGORIEN) {
    let score = 0;
    for (const kw of kat.keywords) {
      if (lower.includes(kw)) score++;
    }
    if (score > bestScore) {
      bestScore = score;
      bestKategorie = kat;
    }
  }

  // Vorhandene Schlüsselinfos erkennen
  const vorhandeneInfos = [];
  const fehlendeInfos = [];
  for (const info of SCHLUESSELINFOS) {
    const gefunden = info.keywords.some(kw => lower.includes(kw));
    if (gefunden) {
      vorhandeneInfos.push(info);
    } else {
      fehlendeInfos.push(info);
    }
  }

  if (!bestKategorie && vorhandeneInfos.length === 0) return null;

  // Tipps: kategoriespezifische zuerst, dann allgemeine fehlende Infos
  const tipps = [];
  if (bestKategorie) {
    for (const tipp of bestKategorie.tipps) {
      // Nur Tipps anzeigen, die noch nicht im Text sind
      const tippLower = tipp.toLowerCase();
      const bereitsVorhanden = vorhandeneInfos.some(v =>
        tippLower.includes(v.label.toLowerCase())
      );
      if (!bereitsVorhanden) {
        tipps.push(tipp);
      }
    }
  }

  return {
    kategorie: bestKategorie,
    vorhandeneInfos,
    fehlendeInfos,
    tipps: tipps.slice(0, 3),
  };
}

const BUNDESLAENDER = [
  'Bayern', 'Baden-Württemberg', 'Niedersachsen', 'Nordrhein-Westfalen',
  'Hessen', 'Sachsen', 'Thüringen', 'Brandenburg', 'Rheinland-Pfalz',
  'Schleswig-Holstein', 'Sachsen-Anhalt', 'Mecklenburg-Vorpommern',
  'Saarland',
];

const FLAECHEN = ['2 ha', '5 ha', '8 ha', '15 ha', '25 ha', '50 ha'];

// Mini-Formular Komponente für 1-Klick-Vorlagen
function VorlageFormular({ kategorie, onAbsenden, t }) {
  const [bundesland, setBundesland] = useState('');
  const [flaeche, setFlaeche] = useState('');
  const [offen, setOffen] = useState(false);

  const befuellteVorlage = kategorie.vorlage
    .replace(/___\s*ha\b/, flaeche || '___ ha')
    .replace(/in\s*___/, `in ${bundesland || '___'}`)
    .replace(/ca\.\s*___\s*Jahre/, 'ca. ___ Jahre')
    .replace(/seit\s*___/, 'seit ___')
    .replace(/___\s*Festmeter/, '___ Festmeter')
    .replace(/BHD ca\.\s*___\s*cm/, 'BHD ca. ___ cm');

  return (
    <div className="w-full mb-2 rounded-lg border border-primary/20 bg-primary/[0.04] overflow-hidden" data-testid="vorlage-formular">
      {/* Header / Toggle */}
      <button
        onClick={() => setOffen(!offen)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-primary/[0.06] transition-colors"
        data-testid="eingabe-vorlage-btn"
      >
        <FileText className="h-4 w-4 text-primary shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-[11px] font-medium text-primary block">{t('eingabeAssistent.vorlage')}</span>
          <span className="text-[10px] text-muted-foreground block truncate">{kategorie.label}</span>
        </div>
        <ChevronDown className={`h-3.5 w-3.5 text-primary transition-transform ${offen ? 'rotate-180' : ''}`} />
      </button>

      {/* Formular */}
      {offen && (
        <div className="px-3 pb-3 space-y-2.5 border-t border-primary/10 pt-2.5 animate-in fade-in-0 slide-in-from-top-1 duration-200">
          {/* Bundesland */}
          <div>
            <label className="text-[10px] text-muted-foreground font-medium block mb-1">{t('eingabeAssistent.formBundesland')}</label>
            <select
              value={bundesland}
              onChange={(e) => setBundesland(e.target.value)}
              className="w-full text-xs px-2.5 py-1.5 rounded-md border border-warm-200 bg-white text-foreground focus:border-primary focus:ring-1 focus:ring-primary/20 outline-none"
              data-testid="vorlage-bundesland"
            >
              <option value="">{t('eingabeAssistent.formBundeslandWaehlen')}</option>
              {BUNDESLAENDER.map(bl => (
                <option key={bl} value={bl}>{bl}</option>
              ))}
            </select>
          </div>

          {/* Fläche */}
          <div>
            <label className="text-[10px] text-muted-foreground font-medium block mb-1">{t('eingabeAssistent.formFlaeche')}</label>
            <div className="flex flex-wrap gap-1.5">
              {FLAECHEN.map(f => (
                <button
                  key={f}
                  onClick={() => setFlaeche(f)}
                  className={`text-[11px] px-2.5 py-1 rounded-md border transition-colors ${
                    flaeche === f
                      ? 'border-primary bg-primary/10 text-primary font-medium'
                      : 'border-warm-200 bg-white text-foreground hover:border-primary/30'
                  }`}
                  data-testid={`vorlage-flaeche-${f.replace(' ', '')}`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          {/* Vorschau */}
          <div className="px-2.5 py-2 rounded-md bg-warm-50 border border-warm-200">
            <p className="text-[10px] text-muted-foreground mb-0.5">{t('eingabeAssistent.formVorschau')}</p>
            <p className="text-xs text-foreground leading-relaxed" data-testid="vorlage-vorschau">{befuellteVorlage}</p>
          </div>

          {/* Übernehmen */}
          <button
            onClick={() => onAbsenden(befuellteVorlage)}
            className="w-full flex items-center justify-center gap-1.5 text-xs font-medium px-3 py-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
            data-testid="vorlage-uebernehmen-btn"
          >
            <Send className="h-3 w-3" />
            {t('eingabeAssistent.formUebernehmen')}
          </button>
        </div>
      )}
    </div>
  );
}

export function EingabeAssistent({ freitext, onTippKlick, onVorlageKlick, t }) {
  const [analyse, setAnalyse] = useState(null);
  const [vorlageVerwendet, setVorlageVerwendet] = useState(false);
  const timerRef = useRef(null);

  // Debounced Analyse
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setAnalyse(analyzeText(freitext));
    }, 400);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [freitext]);

  if (!analyse || (!analyse.kategorie && analyse.vorhandeneInfos.length === 0)) return null;

  const alleDa = analyse.tipps.length === 0 && analyse.fehlendeInfos.length <= 1;

  return (
    <div
      className="mt-3 p-3.5 rounded-xl border border-primary/15 bg-primary/[0.02] animate-in fade-in-0 slide-in-from-bottom-2 duration-300"
      data-testid="eingabe-assistent"
    >
      {/* Erkannte Kategorie */}
      {analyse.kategorie && (
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-3.5 w-3.5 text-primary" />
          <span className="text-xs font-medium text-primary">
            {t('eingabeAssistent.erkannt')}: {analyse.kategorie.label}
          </span>
        </div>
      )}

      {/* 1-Klick-Vorlage mit Mini-Formular */}
      {analyse.kategorie?.vorlage && !vorlageVerwendet && freitext.length < 40 && (
        <VorlageFormular
          kategorie={analyse.kategorie}
          onAbsenden={(text) => {
            onVorlageKlick && onVorlageKlick(text);
            setVorlageVerwendet(true);
          }}
          t={t}
        />
      )}

      {/* Vorhandene Infos (grüne Häkchen) */}
      {analyse.vorhandeneInfos.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-2">
          {analyse.vorhandeneInfos.map(info => (
            <span key={info.id} className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
              <CheckCircle2 className="h-3 w-3" />
              {info.label}
            </span>
          ))}
        </div>
      )}

      {/* Fehlende Infos als Tipps */}
      {!alleDa && analyse.tipps.length > 0 && (
        <div className="mt-2">
          <p className="text-[11px] text-muted-foreground mb-1.5 flex items-center gap-1">
            <Lightbulb className="h-3 w-3" />
            {t('eingabeAssistent.tipp')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {analyse.tipps.map((tipp, i) => (
              <button
                key={i}
                onClick={() => onTippKlick && onTippKlick(tipp)}
                className="inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-lg border border-amber-200 bg-amber-50/70 text-amber-800 hover:bg-amber-100 hover:border-amber-300 transition-colors cursor-pointer"
                data-testid={`eingabe-tipp-${i}`}
              >
                <Plus className="h-3 w-3" />
                {tipp}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Alles vollständig */}
      {alleDa && (
        <div className="flex items-center gap-2 text-[11px] text-emerald-700">
          <CheckCircle2 className="h-3.5 w-3.5" />
          {t('eingabeAssistent.vollstaendig')}
        </div>
      )}
    </div>
  );
}
